import { styled } from '@mui/material';
import { Text, useStacklineTheme } from '@stackline/ui';
import React, { CSSProperties, useState } from 'react';
import { Link } from 'react-router-dom';
import UnstyledButton from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/UnstyledFilterButton';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import { DataObject } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Ineligible/types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import MaskedInput from 'src/components/BeaconV2Components/common/StyledInput/MaskedInput';
import { integerMask } from 'src/components/BeaconV2Components/common/StyledInput/inputMasks';
import { BeaconProImageFallback, SelectExcludeCheckbox, SelectedIncludeCheckbox } from 'src/components/SvgIcons';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { getStartAndEndOfWeek } from 'src/utils/dateUtils';
import { formatTime } from 'src/utils/dateformatting';
import { getBrandImageUrl, getProductImageUrl } from 'src/utils/image';
import { splitCamelCase } from 'src/utils/stringFormatting';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import PortfolioStore from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/PortfolioBuilder/stores';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import { DynamicDownChevron } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/DynamicDownChevron';
import { SlMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import numeral from 'numeral';
import _clone from 'lodash/clone';
import { useDispatch } from 'react-redux';
import { fetchAdCampaigns } from 'src/store/modules/adManager/adCampaigns/operations';
import { CustomLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import _startCase from 'lodash/startCase';
import _get from 'lodash/get';
import { fetchAdPortfolios } from 'src/store/modules/adManager/adPortfolios/operations';
import {
  tupleMapLookup,
  campaignStatusMatches,
  updateCampaign,
  updatePortfolio
} from 'src/components/AdManager/AmsUtils';
import RuleStore from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesDetails/stores';

export const getCompareValue = (data: any, partialKey: string) => {
  if (!data) {
    return 0;
  }
  const dataKey = Object.keys(data).find((key) => key.includes(partialKey));
  const comparisonValue = data[dataKey] || 0;
  return comparisonValue;
};

const TableP = styled('p')({
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': 2,
  overflow: 'hidden',
  'word-break': 'break-word',
  margin: 0
});

export const TableSpan = styled('span')({
  margin: 0
});

export const ProductColumnDefinition = {
  headerName: 'Products',
  valueFormatter: (_, row: { product: any }) => (
    <div className="product-col" style={{ width: '260px' }}>
      <ProductCell key={row.product?.title} stacklineSku={row.product?.stacklineSku} title={row.product?.title} />
    </div>
  )
};

export const IneligibleProductColumnDefinition = {
  headerName: 'Products',
  valueFormatter: (_, row: DataObject) => (
    <div style={{ width: '260px' }}>
      <ProductCell
        key={row.additionalMetaData.stacklineSku}
        stacklineSku={row.additionalMetaData.stacklineSku}
        title={row.additionalMetaData.product.title}
        overRideAdditionalParams="&amp;tab=advertising&amp;subtab=adMetrics"
      />
    </div>
  )
};

export const IneligibleRetailerSkuDefinition = {
  headerName: 'RETAILER SKU',
  valueFormatter: (_, row: DataObject) => {
    return <Text variant="body2">{row.additionalMetaData.product?.retailerSku}</Text>;
  }
};

const otherStatus = new Set(['ineligible', 'missingImage', 'notBuyable', 'missingBuyBox']);

export const IneligibleProductStatusDefinition = {
  headerName: 'STATUS',
  valueFormatter: (_, row: DataObject) => {
    let label = splitCamelCase(row.additionalValues?.statusReason);
    label = label.charAt(0).toUpperCase() + label.slice(1);
    return (
      <div>
        <Text variant="body2">Ineligible</Text>
        <div
          style={{
            marginTop: '4px'
          }}
        >
          <Text variant="body3" opacity={50}>
            {otherStatus.has(row.additionalValues?.statusReason) ? 'Other' : label}
          </Text>
        </div>
      </div>
    );
  }
};

export const IneligibleProductAdClicksDefinition = {
  headerName: 'Ad Clicks',
  valueFormatter: (_, row: DataObject) => {
    const currentValue = row?.additionalValues?.clicks_sum_value;
    const comparisonValue = getCompareValue(row?.additionalValues, 'clicks_sum_value_');

    return (
      <MetricCell
        value={currentValue}
        metricType={METRICTYPE.MONEY}
        percentChange={calculatePercentChange(currentValue, comparisonValue)}
      />
    );
  }
};

export const IneligibleProductAdSpendDefinition = {
  headerName: 'Ad Spend',
  valueFormatter: (_, row: DataObject) => {
    const currentValue = row?.additionalValues?.spend_sum_value;
    const comparisonValue = getCompareValue(row?.additionalValues, 'spend_sum_value_');

    return (
      <MetricCell
        value={currentValue}
        metricType={METRICTYPE.MONEY}
        percentChange={calculatePercentChange(currentValue, comparisonValue)}
        isAdSpendMetric
      />
    );
  }
};

export const IneligibleProductAdSalesDefinition = {
  headerName: 'Ad Sales',
  valueFormatter: (_, row: DataObject) => {
    const currentValue = row?.additionalValues?.sales_sum_value;
    const comparisonValue = getCompareValue(row?.additionalValues, 'sales_sum_value_');

    return (
      <MetricCell
        value={currentValue}
        metricType={METRICTYPE.MONEY}
        percentChange={calculatePercentChange(currentValue, comparisonValue)}
      />
    );
  }
};

export const IneligibleProductROASDefinition = {
  headerName: 'ROAS',
  valueFormatter: (_, row: DataObject) => {
    const currentValue = row?.additionalValues?.returnOnAdSpend_computed_value;
    const comparisonValue = getCompareValue(row?.additionalValues, 'returnOnAdSpend_computed_value_');

    return (
      <MetricCell
        value={currentValue}
        metricType={METRICTYPE.MONEY}
        percentChange={calculatePercentChange(currentValue, comparisonValue)}
      />
    );
  }
};

export const AdProductColumnDefinition = {
  headerName: 'Products',
  valueFormatter: (_, row: { product: ProductEntity }) => (
    <div style={{ width: '260px' }}>
      <Flex gap="md" alignItems="center">
        <img
          key={row.product?.stacklineSku}
          style={{
            minWidth: '30px',
            maxWidth: '30px',
            maxHeight: '30px',
            objectFit: 'cover'
          }}
          src={getProductImageUrl(row.product?.stacklineSku)}
          alt={row.product.title}
        />

        <Text variant="body2" truncateLines={2} title={row.product?.title}>
          {row.product?.title}
        </Text>
      </Flex>
    </div>
  )
};

interface GetWeekColumnDefinitionProps {
  headerName?: string;
  getTableColumnProps?: (defaults: { style: { paddingLeft: string; width: string } }) => any;
  valueFormatter?: (_: any, row: any) => React.ReactElement;
}

/**
 * Function that returns the column definition for the week ending column used in MetricListContainers.
 * Allows for easier modification of the column definition for styling/formatting.
 */
export const getWeekColumnDefinition = ({
  headerName = 'Week Ending',
  getTableColumnProps = null,
  valueFormatter = (_, row: any) => {
    const { weekId } = row;
    const { endDate } = getStartAndEndOfWeek(weekId);
    const weekEndingDate = formatTime(endDate, 'MMM D, YYYY');
    return (
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ margin: 0 }} title={weekEndingDate}>
          {weekEndingDate}
        </span>
      </div>
    );
  }
}: GetWeekColumnDefinitionProps) => {
  const defaultTableColumnProps = {
    style: {
      paddingLeft: '24px',
      width: '500px'
    }
  };

  const tableColumnProps = getTableColumnProps ? getTableColumnProps(defaultTableColumnProps) : defaultTableColumnProps;

  return {
    headerName,
    tableColumnProps,
    valueFormatter
  };
};

export const EntityLink = ({
  id,
  children,
  entityType,
  className
}: {
  id: string;
  children: React.ReactNode;
  entityType: string;
  className?: string;
}) => {
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  return (
    <Link className={className} to={`/${entityType}/${id}${searchParams}${additionalParams}`}>
      {children}
    </Link>
  );
};

export const CategoryColumnDefinition = {
  headerName: 'Categories',
  valueFormatter: (_, row: { category: { id: string; name: string } }) => (
    <EntityLink id={row.category.id} entityType="category">
      {row.category.name}
    </EntityLink>
  )
};

export const SubcategoryColumnDefinition = {
  headerName: 'Subcategories',
  valueFormatter: (_, row: { subcategory: { id: string; name: string } }) => (
    <EntityLink id={row.subcategory.id} entityType="subcategory">
      {row.subcategory.name}
    </EntityLink>
  )
};

const brandColumnImageStyles: CSSProperties = {
  width: '30px',
  height: '30px',
  marginRight: '12px',
  objectFit: 'contain'
};

export const BrandColumnDefinition = {
  headerName: 'Brands',
  valueFormatter: (_, row: { brand: { id: string; name: string } }) => {
    const Image = () => {
      const [error, setError] = useState(false);

      if (error) {
        return <BeaconProImageFallback style={brandColumnImageStyles} />;
      }

      return (
        <img
          src={getBrandImageUrl(row.brand.id)}
          style={brandColumnImageStyles}
          alt={row.brand.name}
          onError={() => setError(true)}
        />
      );
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Image />
        <EntityLink id={row.brand.id} entityType="brand">
          {row.brand.name}
        </EntityLink>
      </div>
    );
  }
};

export const EntityColumnDefinition = {
  headerName: 'Entities',
  valueFormatter: (_, row: { entity: { entityId; entityName } }) => (
    <EntityLink id={row?.entity?.entityId} entityType="adEntity">
      {row.entity?.entityName}
    </EntityLink>
  )
};

export const CampaignColumnDefinitionNewTab = {
  headerName: 'Campaigns',
  valueFormatter: (_, row: { campaign: { campaignId; campaignName; retailerId } }) => (
    <div
      style={{
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: '164px',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2
      }}
    >
      <Link
        id={row?.campaign?.campaignId}
        target="_blank"
        to={`/adCampaign/${row?.campaign?.campaignId}?tab=advertising&subtab=adMetrics&rid=${row?.campaign?.retailerId}`}
      >
        {row.campaign?.campaignName}
      </Link>
    </div>
  )
};

export const CampaignColumnDefinition = {
  headerName: 'Campaigns',
  valueFormatter: (_, row: { campaign: { campaignId; campaignName; retailerId } }) => {
    return (
      <div
        style={{
          wordBreak: 'break-word',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: '164px',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2
        }}
      >
        <Link
          id={row?.campaign?.campaignId}
          to={`/adCampaign/${row?.campaign?.campaignId}?tab=advertising&subtab=adMetrics&rid=${row?.campaign?.retailerId}`}
        >
          {row.campaign?.campaignName}
        </Link>
      </div>
    );
  }
};

export const AdGroupColumnDefinition = {
  headerName: 'Ad Group',
  valueFormatter: (_, { adGroup: { adGroupId, adGroupName } }) => (
    <div
      style={{
        maxWidth: '77px'
      }}
    >
      <TableP title={adGroupId}>{splitCamelCase(adGroupName)}</TableP>
    </div>
  )
};

export const CampaignTypeForCampaignColumnDefinition = {
  headerName: 'Campaign Type',
  valueFormatter: (_, { campaign: { campaignType } }) => (
    <div
      style={{
        maxWidth: '77px'
      }}
    >
      <TableP title={campaignType}>{splitCamelCase(campaignType)}</TableP>
    </div>
  )
};

export const CampaignStatusColumnDefinition = {
  headerName: 'Campaign Status',
  valueFormatter: (_, { campaign: { state } }) => <Text variant="body2">{state}</Text>
};

export const KeywordColumnDefinition = {
  headerName: 'Keyword',
  valueFormatter: (_, row) => (
    <div
      className="keyword-col"
      style={{ width: '135px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      <TableSpan title={row.name}>{row.name}</TableSpan>
    </div>
  )
};

export const CampaignTypeColumnDefinition = {
  headerName: 'Campaign Type',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{splitCamelCase(row.name)}</Text>;
  }
};

enum TargetingType {
  EXACT = 'exact',
  PHRASE = 'phrase',
  BROAD = 'broad',
  PRODUCT = 'product',
  AUTO_TARGETING = 'auto_targeting',
  CATEGORY = 'category',
  KEYWORD_TARGETING = 'keyword_targeting',
  PRODUCT_TARGETING = 'product_targeting'
}

const formatTargetingType = (value: TargetingType) => {
  switch (value) {
    case TargetingType.EXACT:
      return 'Exact match';
    case TargetingType.PHRASE:
      return 'Phrase match';
    case TargetingType.BROAD:
      return 'Broad match';
    case TargetingType.PRODUCT:
      return 'Product';
    case TargetingType.AUTO_TARGETING:
      return 'Auto Targeting';
    case TargetingType.CATEGORY:
      return 'Category';
    case TargetingType.KEYWORD_TARGETING:
      return 'Keyword Targeting';
    case TargetingType.PRODUCT_TARGETING:
      return 'Product Targeting';
    default:
      return value;
  }
};

export const TargetTypeColumnDefinition = {
  headerName: 'Match Type',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{formatTargetingType(row.targetingType)}</Text>;
  }
};

export const PortfolioColumnDefinition = {
  headerName: 'Portfolios',
  valueFormatter: (_, row: { portfolio: { portfolioId; portfolioName } }) => (
    <EntityLink id={row.portfolio?.portfolioId} entityType="adPortfolio">
      {row.portfolio?.portfolioName}
    </EntityLink>
  )
};

export const TargetColumnDefinition = {
  headerName: 'Targets',
  valueFormatter: (_, row: { targetingText; name }) => {
    return <Text variant="body2">{row.name}</Text>;
  }
};

export const OptimizationColumnDefinition = {
  headerName: 'Campaign',
  valueFormatter: (_, row) => {
    return <Text variant="body2">{row.campaignName}</Text>;
  }
};

const VolumeEditCellContainer = styled('div')({
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  position: 'relative',
  '.cell_input_wrapper': {
    boxSizing: 'content-box',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 32,
    boxShadow: '0px 0px 0px 0px rgba(0, 0, 0, 0)',
    background: '#ffffff',
    borderRadius: 8,
    padding: 0,
    marginLeft: 0,
    '.button_group': {
      width: 0,
      opacity: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 4,
      transition: 'all 0.3s ease-in'
    },
    '&.cell_focus': {
      marginLeft: -16,
      padding: 16,
      boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.10)',
      background: '#FBFBFB',
      height: 32,
      '.button_group': {
        overflow: 'hidden',
        width: 48,
        opacity: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 4
      }
    }
  },
  '.cell_input_container': {
    zIndex: 1,
    overflow: 'hidden',
    height: 32,
    padding: '0 8px',
    gap: '2px',
    borderRadius: 4,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease-in',
    input: {
      flex: 1,
      flexBasis: '100%',
      minWidth: 0,
      outline: 'none',
      border: 'none',
      textAlign: 'right'
    }
  }
});

export const VolumnEditCell = ({
  prefix,
  defaultValue,
  placeholder,
  saveValue,
  baseWidth = 88,
  min,
  max,
  maxErrorMessage,
  minErrorMessage
}: {
  min?: number;
  max?: number;
  minErrorMessage?: string | ((value: string) => string);
  maxErrorMessage?: string | ((value: string) => string);
  prefix?: string;
  defaultValue?: string;
  placeholder?: string;
  saveValue?: (value: string) => void;
  baseWidth?: number;
}) => {
  const { showSnackbar } = useSnackbar();
  const [error, setError] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [isFocus, setIsFocus] = useState(false);
  const minError = typeof minErrorMessage === 'function' ? minErrorMessage(value) : minErrorMessage;
  const maxError = typeof maxErrorMessage === 'function' ? maxErrorMessage(value) : maxErrorMessage;

  const onSave = () => {
    // Check if value is greater than min and less than max
    if (min && +value < min) {
      setError(true);
      showSnackbar({
        message: minError,
        type: 'error'
      });
    } else if (max && +value > max) {
      setError(true);
      showSnackbar({
        message: maxError,
        type: 'error'
      });
    } else {
      if (saveValue) {
        saveValue(value);
      }
      setIsFocus(false);
      setError(false);
    }
  };
  return (
    <VolumeEditCellContainer style={{ width: baseWidth }}>
      <div className={`cell_input_wrapper ${isFocus ? 'cell_focus' : ''}`}>
        <div className="cell_input_container" style={{ width: baseWidth, borderColor: error ? '#FF534A' : '#E8E8ED' }}>
          {prefix}
          <MaskedInput
            masker={integerMask}
            value={value}
            onChange={(e) => {
              setError(false);
              setValue(e.target.value);
            }}
            placeholder={placeholder}
            style={{ height: 26 }}
            autoComplete="off"
            id="type-search"
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={() => {
              if (!value || value === defaultValue) {
                setIsFocus(false);
                setError(false);
              }
            }}
          />
        </div>
        <div className="button_group">
          <UnstyledButton onClick={onSave}>
            <SelectedIncludeCheckbox
              style={{
                width: '16px',
                height: '16px'
              }}
            ></SelectedIncludeCheckbox>
          </UnstyledButton>
          <UnstyledButton
            onClick={() => {
              setValue(defaultValue);
              setIsFocus(false);
            }}
          >
            <SelectExcludeCheckbox
              style={{
                width: '16px',
                height: '16px'
              }}
            ></SelectExcludeCheckbox>
          </UnstyledButton>
        </div>
      </div>
    </VolumeEditCellContainer>
  );
};

const SelectCellContainer = styled('div')({
  zIndex: 1,
  overflow: 'hidden',
  height: 32,
  padding: '0 8px',
  gap: '2px',
  borderRadius: 4,
  border: '1px solid #E8E8ED',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

export const SelectDropdownCell = ({
  onSave,
  selection,
  dropdownOptions
}: {
  onSave: (id: string) => void;
  selection?: {
    id: string;
    label: string;
  };

  dropdownOptions: { id: string; label: string }[];
}) => {
  const theme = useStacklineTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <SelectCellContainer>
      <button
        onClick={handleClick}
        style={{
          width: '100%',
          background: 'none',
          padding: '0',
          color: theme.colors.primary,
          outline: 'none',
          border: 'none',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ fontWeight: 400 }}> {selection?.label || ''}</div>

        <div>
          {anchorEl ? (
            <DynamicDownChevron
              strokeWidth="2px"
              color={theme.colors.primary}
              style={{ transform: 'rotate(180deg)', verticalAlign: 'bottom' }}
            />
          ) : (
            <DynamicDownChevron strokeWidth="2px" color={theme.colors.primary} style={{ verticalAlign: 'bottom' }} />
          )}
        </div>
      </button>
      <SlMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        options={dropdownOptions}
        selectedId={selection?.id}
        onChange={(opt) => {
          onSave(opt.id);
        }}
        sx={{
          marginTop: '6px',
          '& ul': {
            boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.15)',
            paddingY: 0
          },
          '.MuiPaper-root': {
            maxHeight: 'calc(100% - 59px)',

            paddingRight: 0,
            boxSizing: 'border-box',
            '&::-webkit-scrollbar': { display: 'none' }
          },
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      />
    </SelectCellContainer>
  );
};

const CampaignBudgetEditCell = ({ campaign }: any) => {
  const { showSnackbar } = useSnackbar();
  const adCampaigns = useAppSelector((state) => state.adCampaigns);
  const currentCampaign: any = adCampaigns.find((item) => item.id === campaign.campaignId);
  const [loading, setLoading] = useState(false);
  const currentBudget = currentCampaign?.extendedAttributes?.liveBudgetSetting?.amount || 0;
  const dispatch = useDispatch();

  const placeholderText = currentBudget ? numeral(currentBudget).format('1,000') : '0';
  const onBudgetChange = async (value) => {
    try {
      const mainEntityCloned: any = _clone(currentCampaign);
      delete mainEntityCloned.adCampaignProducts;
      delete mainEntityCloned.adTargets;
      delete mainEntityCloned.adGroups;
      mainEntityCloned.extendedAttributes.currentMonthBudgetSetting = {
        ...mainEntityCloned.extendedAttributes.currentMonthBudgetSetting,
        amount: +value || 1
      };
      setLoading(true);

      await updateCampaign(mainEntityCloned);
      await dispatch(fetchAdCampaigns());
      showSnackbar({
        message: 'Budget saved successfully.',
        type: 'success'
      });

      setLoading(false);
    } catch (error) {
      showSnackbar({
        message: 'Error saving budget, please try again later.',
        type: 'error'
      });
      console.error('Error saving budget', error);
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return <CustomLoading height={32} width={88} padding={0} />;
  }
  return (
    <div style={{ width: 88 }} key={campaign.campaignId}>
      <VolumnEditCell
        minErrorMessage="Your new budget cannot be less than MTD ad spend. Please increase your budget."
        baseWidth={88}
        prefix="$"
        defaultValue={undefined}
        placeholder={placeholderText}
        saveValue={onBudgetChange}
      />
    </div>
  );
};

const shortenAutomationType = (automationType: string) => {
  return automationType?.replace('Maximize', 'Max');
};

const PortfolioBudgetEditCell = ({ portfolio }: any) => {
  const { showSnackbar } = useSnackbar();
  const adPortfolios: any[] = useAppSelector((state) => state.adPortfolios);
  const adPortfoliosData = useAppSelector((state) => state.adPortfoliosData);

  const mtdSpend = adPortfoliosData?.mtdSpend?.find((item) => item.fieldId === portfolio.portfolioId)?.value || 0;

  const currentPortfolio = adPortfolios.find((item) => item.id === portfolio.portfolioId);
  const [loading, setLoading] = useState(false);
  const currentBudget = currentPortfolio?.extendedAttributes?.liveBudgetSetting?.amount || 0;
  const dispatch = useDispatch();

  const placeholderText = currentBudget ? numeral(currentBudget).format('1,000') : '0';
  const onBudgetChange = async (value) => {
    try {
      const mainEntityCloned: any = _clone(currentPortfolio);

      mainEntityCloned.extendedAttributes.currentMonthBudgetSetting = {
        ...mainEntityCloned.extendedAttributes.currentMonthBudgetSetting,
        amount: +value || 1
      };
      setLoading(true);

      await updatePortfolio(mainEntityCloned);
      await dispatch(fetchAdPortfolios());
      showSnackbar({
        message: 'Budget saved successfully.',
        type: 'success'
      });

      setLoading(false);
    } catch (error) {
      showSnackbar({
        message: 'Error saving budget, please try again later.',
        type: 'error'
      });
      console.error('Error saving budget', error);
    } finally {
      setLoading(false);
    }
  };
  const formattedMtdSpend = numeral(mtdSpend).format('$0,0');
  if (loading) {
    return <CustomLoading height={32} width={88} padding={0} />;
  }
  return (
    <div style={{ width: 88 }} key={portfolio.portfolioId}>
      <VolumnEditCell
        minErrorMessage={`Your new budget cannot be less than MTD ad spend ${formattedMtdSpend}. Please increase your budget.`}
        baseWidth={88}
        min={mtdSpend}
        prefix="$"
        defaultValue={undefined}
        placeholder={placeholderText}
        saveValue={onBudgetChange}
      />
    </div>
  );
};

const PortfolioAutomationCell = ({ portfolio }: any) => {
  const { showSnackbar } = useSnackbar();
  const adPortfolios: any[] = useAppSelector((state) => state.adPortfolios);
  const currentPortfolio = adPortfolios.find((item) => item.id === portfolio.portfolioId);

  const adPlatformSettings = useAppSelector((state) => state.adPlatformSettings);

  const adStrategies = adPlatformSettings.filter((item) => item.settingType === 'campaignStrategy');

  const dropdownOptions = adStrategies.map((strategy) => ({
    id: strategy.id,
    label: shortenAutomationType(strategy.name)
  }));

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const currentStrategyId = currentPortfolio?.extendedAttributes?.automationAttributes?.strategyId;
  const currentStrategy = adStrategies.find((strategy) => strategy.id === currentStrategyId);

  const onSave = async (id: string) => {
    if (id === currentStrategyId) {
      return;
    }
    try {
      const mainEntityCloned: any = _clone(currentPortfolio);
      mainEntityCloned.extendedAttributes.automationAttributes.strategyId = id;
      setLoading(true);

      await updatePortfolio(mainEntityCloned);

      await dispatch(fetchAdPortfolios());
      showSnackbar({
        message: 'Optimization saved successfully.',
        type: 'success'
      });

      setLoading(false);
    } catch (error) {
      showSnackbar({
        message: 'Error saving optimization, please try again later.',
        type: 'error'
      });
      console.error('Error saving budget', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ width: 194 }} key={portfolio.portfolioId}>
        <CustomLoading height={28} width={194} padding={0} />
      </div>
    );
  }
  return (
    <div style={{ width: 194 }} key={portfolio.portfolioId}>
      <SelectDropdownCell
        onSave={onSave}
        dropdownOptions={dropdownOptions}
        selection={{
          id: currentStrategyId,
          label: shortenAutomationType(currentStrategy?.name)
        }}
      />
    </div>
  );
};

const PortfolioProjectedSpendCell = ({ portfolio }: any) => {
  const adPortfolios: any[] = useAppSelector((state) => state.adPortfolios);
  const currentPortfolio = adPortfolios.find((item) => item.id === portfolio.portfolioId);
  const adPortfoliosData = useAppSelector((state) => state.adPortfoliosData);

  const projectedSpend =
    adPortfoliosData?.projectedSpend?.find((item) => item.fieldId === portfolio.portfolioId)?.value || 0;

  const { displayName: statusName, color: statusColor } = _get(currentPortfolio, 'extendedAttributes.statusDisplay', {
    displayName: '',
    color: ''
  });

  const color = statusName === 'delivering' ? '#71c184' : statusName === 'outOfBudget' ? '#ff534a' : statusColor;
  const formattedProjectedSpend = numeral(projectedSpend).format('$0,0');

  return (
    <div style={{ width: 75 }}>
      <div style={{ fontSize: 14 }}>{adPortfoliosData?.projectedSpend ? formattedProjectedSpend : ''}</div>
      <div style={{ color, fontSize: 12 }}>{_startCase(statusName)}</div>
    </div>
  );
};

export const CampaignBudgetField = {
  headerName: 'Daily\nBudget',
  valueFormatter: (_, row) => {
    return <CampaignBudgetEditCell campaign={row} />;
  }
};

export const CampaignAutomationField = {
  headerName: 'Automation\nType',
  valueFormatter: (_, row) => {
    const automationType = row?.campaign?.automationStrategyId || '-';

    const automationDisplay = shortenAutomationType(automationType);
    return <div style={{ width: 110 }}>{_startCase(automationDisplay)}</div>;
  }
};

export const PortfolioBudgetField = {
  headerName: 'Monthly\nBudget',
  valueFormatter: (_, row) => {
    return <PortfolioBudgetEditCell portfolio={row} />;
  }
};

export const PortfolioAutomationField = {
  headerName: 'Automation\ntype',
  valueFormatter: (_, row) => {
    return <PortfolioAutomationCell portfolio={row} />;
  }
};

export const PortfolioProjectedSpendField = {
  headerName: 'Projected\nAd Spend',
  valueFormatter: (_, row) => {
    return <PortfolioProjectedSpendCell portfolio={row} />;
  }
};

const RulesAddRemoveAllCheckBox = () => {
  const {
    ruleTarget,
    addAllSelectedTargets,
    removeAllSelectedTargets,
    addAllSelectedCampaigns,
    removeAllSelectedCampaigns,
    campaigns,
    targets
  } = RuleStore((s) => ({
    ruleTarget: s.rules.ruleTarget,
    addAllSelectedTargets: s.addAllSelectedTargets,
    removeAllSelectedTargets: s.removeAllSelectedTargets,
    addAllSelectedCampaigns: s.addAllSelectedCampaigns,
    removeAllSelectedCampaigns: s.removeAllSelectedCampaigns,
    campaigns: s.campaigns,
    targets: s.targets
  }));

  const [checkedTarget, setCheckedTarget] = useState(false);
  const [checkedCampaign, setCheckedCampaign] = useState(false);

  const addFn = ruleTarget === 'adCampaign' ? addAllSelectedCampaigns : addAllSelectedTargets;
  const removeFn = ruleTarget === 'adCampaign' ? removeAllSelectedCampaigns : removeAllSelectedTargets;
  const checked = ruleTarget === 'adCampaign' ? checkedCampaign : checkedTarget;
  const setChecked = ruleTarget === 'adCampaign' ? setCheckedCampaign : setCheckedTarget;
  const data = ruleTarget === 'adCampaign' ? campaigns : targets;

  if (data.length === 0) {
    return null;
  }

  return (
    <CheckboxItem
      checked={checked}
      label=""
      value="all"
      onClick={(e) => {
        if (e.target.checked) {
          addFn();
        } else {
          removeFn();
        }
        setChecked(e.target.checked);
      }}
    />
  );
};

export const RulesCampaignCheckBox = {
  headerName: <RulesAddRemoveAllCheckBox />,
  valueFormatter: (_, row) => {
    const {
      ruleTarget,
      selectedCampaigns,
      addSelectedCampaigns,
      removeSelectedCampaigns,
      selectedTargets,
      addSelectedTargets,
      removeSelectedTargets
    } = RuleStore((state) => ({
      ruleTarget: state.rules.ruleTarget,
      selectedCampaigns: state.selectedCampaigns,
      addSelectedCampaigns: state.addSelectedCampaigns,
      removeSelectedCampaigns: state.removeSelectedCampaigns,
      selectedTargets: state.selectedTargets,
      addSelectedTargets: state.addSelectedTargets,
      removeSelectedTargets: state.removeSelectedTargets
    }));

    const key = ruleTarget === 'adCampaign' ? row?.campaignId : `${row?.targetingText}_${row?.targetingType}`;

    const checked = ruleTarget === 'adCampaign' ? selectedCampaigns.has(key) : selectedTargets.has(key);
    const addFn = ruleTarget === 'adCampaign' ? addSelectedCampaigns : addSelectedTargets;
    const removeFn = ruleTarget === 'adCampaign' ? removeSelectedCampaigns : removeSelectedTargets;

    return (
      <CheckboxItem
        key={key}
        value={key}
        label=""
        checked={checked}
        onClick={(e) => {
          if (e.target.checked) {
            addFn(row);
          } else {
            removeFn(key);
          }
        }}
      />
    );
  }
};

const PortfolioAddRemoveAllCheckbox = () => {
  const { campaignIds, removeAllCampaignIds, addAllCampaignIds, searchTerm } = PortfolioStore((s) => ({
    campaignIds: s.campaignIds,
    removeAllCampaignIds: s.removeAllCampaignIds,
    addAllCampaignIds: s.addAllCampaignIds,
    searchTerm: s.searchTerm
  }));

  const [checkedCampaign, setCheckedCampaign] = useState(false);

  if (campaignIds.size === 0) {
    return null;
  }

  return (
    <CheckboxItem
      checked={checkedCampaign}
      label=""
      value="all"
      onClick={(e) => {
        if (e.target.checked) {
          addAllCampaignIds();
        } else {
          removeAllCampaignIds();
        }
        setCheckedCampaign(e.target.checked);
      }}
    />
  );
};

export const PortfolioCampaignCheckBox = {
  headerName: <PortfolioAddRemoveAllCheckbox />,
  valueFormatter: (_, row) => {
    const { selectedCampaignIds, selectCampaignId, unselectCampaignId } = PortfolioStore((state) => ({
      selectedCampaignIds: state.selectedCampaignIds,
      selectCampaignId: state.selectCampaignId,
      unselectCampaignId: state.unselectCampaignId
    }));

    const key = row?.campaignId;
    const checked = selectedCampaignIds.has(key);

    return (
      <CheckboxItem
        key={key}
        value={key}
        label=""
        checked={checked}
        onClick={(e) => {
          if (e.target.checked) {
            selectCampaignId(key);
          } else {
            unselectCampaignId(key);
          }
        }}
      />
    );
  }
};

export const CampaignTypeField = {
  headerName: 'Campaign\nType',
  valueFormatter: (_, row) => {
    if (!row?.campaign?.campaignType || !row?.campaign?.statusDerived) {
      return null;
    }
    let campaignType = splitCamelCase(row?.campaign?.campaignType);
    campaignType = campaignType.charAt(0).toUpperCase() + campaignType.slice(1);

    const { color } = tupleMapLookup(
      row?.campaign?.statusDerived,
      row?.campaign?.statusReason,
      campaignStatusMatches as any
    );

    return (
      <div style={{ width: '142px' }}>
        <Text variant="body2">{campaignType}</Text>
        <Text
          variant="body3"
          sx={{
            color
          }}
        >
          {_startCase(row?.campaign?.statusDerived)}
        </Text>
      </div>
    );
  }
};
